import { motion, Variants } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useEffect } from 'react';

export default function Footer() {
  const scrollToSection = (id: string) => {
    if (typeof document === 'undefined') return;
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {}, []);

  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const columnVariants: Variants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94],
      },
    },
  };

  return (
    <footer id="contact" className="bg-brand-green-900 pt-14 sm:pt-16 md:pt-20 pb-8 sm:pb-10 px-5 sm:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Map Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-12 sm:mb-16"
        >
          <h3 className="text-white text-xl sm:text-2xl font-bold mb-6 text-center">Nuestra ubicación</h3>
          <div className="w-full h-64 sm:h-80 lg:h-96 rounded-2xl overflow-hidden shadow-2xl">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3282.0168878433846!2d-58.8281!3d-34.6645!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x95bcc5f7e1b6f1d%3A0x3e7d1f0d6e8f8f8f!2sJuncal%20253%2C%20Merlo%2C%20Buenos%20Aires%2C%20Argentina!5e0!3m2!1ses!2sar!4v1234567890123!5m2!1ses!2sar"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Ubicación del Centro Médico RENOVA"
            />
          </div>
        </motion.div>

        {/* Main Footer Content */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-10 lg:gap-12 mb-10 sm:mb-16"
        >
          {/* Column 1 - Brand */}
          <motion.div variants={columnVariants}>
            <motion.img
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              src="https://wsrv.nl/?url=static.readdy.ai/image/2c86260c8f94482bcf1097c93dc473ba/5ccba9ee78a6b9d633dcc762d6a575d2.png&w=150&output=webp"
              alt="RENOVA Centro Médico"
              className="h-12 sm:h-14 mb-4 sm:mb-5"
              width={150}
              height={56}
              loading="lazy"
            />
            <p className="text-brand-green-200/60 text-xs sm:text-sm mb-6 sm:mb-8 leading-relaxed">
              Centro médico integral con profesionales especializados para el cuidado de tu salud y la de tu familia.
            </p>
            {/* Social Icons */}
            <div className="flex gap-3">
              {[
                { icon: 'ri-facebook-fill', label: 'Facebook', url: 'https://www.facebook.com/profile.php?id=61588432989584', hoverBg: 'hover:bg-[#1877F2]' },
                { icon: 'ri-instagram-line', label: 'Instagram', url: 'https://www.instagram.com/centrorenovamerlo?igsh=MXF1bTZieXIycnd6Ng==', hoverBg: 'hover:bg-gradient-to-tr hover:from-[#F58529] hover:via-[#DD2A7B] hover:to-[#8134AF]' },
                { icon: 'ri-tiktok-line', label: 'TikTok', url: 'https://www.tiktok.com/@cmrenovamerlo', hoverBg: 'hover:bg-black' },
              ].map((social, index) => (
                <motion.a
                  key={social.label}
                  href={social.url}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  aria-label={social.label}
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5 + index * 0.1, duration: 0.4, type: 'spring', stiffness: 200 }}
                  whileHover={{ scale: 1.1, y: -3 }}
                  whileTap={{ scale: 0.95 }}
                  className={`w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-lg bg-brand-green-800/50 text-brand-green-300/70 ${social.hoverBg} hover:text-white transition-all duration-300 cursor-pointer`}
                >
                  <i className={`${social.icon} text-base sm:text-lg`} />
                </motion.a>
              ))}
            </div>
          </motion.div>

          {/* Column 2 - Quick Links */}
          <motion.div variants={columnVariants}>
            <h3 className="text-white text-sm sm:text-base font-bold mb-4 sm:mb-5">Enlaces rápidos</h3>
            <ul className="space-y-2.5 sm:space-y-3">
              {[
                { label: 'Inicio', path: '/' },
                { label: 'Especialidades médicas', path: '/especialidades-medicas' },
                { label: 'Quiénes Somos', path: '/quienes-somos' },
                { label: '¿Por qué Renova?', path: '/por-que-renova' },
                { label: 'Turnos', path: '/turnos' },
                { label: 'Contacto', path: '/contacto' },
              ].map((link, index) => (
                <motion.li
                  key={link.label}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + index * 0.05, duration: 0.4 }}
                >
                  <Link
                    to={link.path}
                    className="text-brand-green-200/60 hover:text-brand-gold-400 transition-all duration-300 text-xs sm:text-sm cursor-pointer inline-block hover:translate-x-1"
                  >
                    {link.label}
                  </Link>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Column 3 - Contact & Hours */}
          <motion.div variants={columnVariants}>
            <h3 className="text-white text-sm sm:text-base font-bold mb-4 sm:mb-5">Contacto</h3>
            <ul className="space-y-4 sm:space-y-5 mb-6">
              <motion.li
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4, duration: 0.4 }}
                className="flex items-start gap-3"
              >
                <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                  <i className="ri-phone-line text-brand-gold-400 text-sm sm:text-base" />
                </div>
                <a
                  href="tel:+5491162450002"
                  className="text-brand-green-200/60 hover:text-brand-gold-300 transition-colors text-xs sm:text-sm cursor-pointer"
                >
                  +54 9 11 6245-0002
                </a>
              </motion.li>
              <motion.li
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5, duration: 0.4 }}
                className="flex items-start gap-3"
              >
                <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                  <i className="ri-mail-line text-brand-gold-400 text-sm sm:text-base" />
                </div>
                <a
                  href="mailto:centromedicorenovasa@gmail.com"
                  className="text-brand-green-200/60 hover:text-brand-gold-300 transition-colors text-xs sm:text-sm cursor-pointer break-all sm:break-normal"
                >
                  centromedicorenovasa@gmail.com
                </a>
              </motion.li>
              <motion.li
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6, duration: 0.4 }}
                className="flex items-start gap-3"
              >
                <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                  <i className="ri-map-pin-line text-brand-gold-400 text-sm sm:text-base" />
                </div>
                <a
                  href="https://www.google.com/maps/search/?api=1&query=Juncal+253,+Merlo,+Buenos+Aires,+Argentina"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-green-200/60 hover:text-brand-gold-300 transition-colors text-xs sm:text-sm cursor-pointer"
                >
                  Juncal 253<br />
                  Merlo, Buenos Aires, Argentina
                </a>
              </motion.li>
            </ul>

            {/* Horarios */}
            <div className="mb-6">
              <h4 className="text-white text-sm font-bold mb-3 flex items-center gap-2">
                <i className="ri-time-line text-brand-gold-400" />
                Horarios de atención
              </h4>
              <div className="space-y-2 text-xs sm:text-sm text-brand-green-200/60">
                <p><strong className="text-brand-green-200">Lunes a Viernes:</strong> 8:00 - 20:00</p>
                <p><strong className="text-brand-green-200">Sábados:</strong> 8:00 - 14:00</p>
                <p><strong className="text-brand-green-200">Domingos:</strong> Cerrado</p>
              </div>
            </div>

            {/* WhatsApp Button */}
            <motion.a
              href="https://wa.me/5491162450002"
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.5 }}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 bg-brand-green-500 text-white text-xs sm:text-sm font-semibold rounded-full hover:bg-brand-green-400 hover:shadow-lg hover:shadow-brand-green-500/25 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line text-base sm:text-lg" />
              Escribinos por WhatsApp
            </motion.a>
          </motion.div>
        </motion.div>

        {/* Bottom Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.6 }}
          className="border-t border-brand-green-800/50 pt-6 sm:pt-8"
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
            <p className="text-brand-green-200/40 text-xs sm:text-sm text-center sm:text-left">
              &copy; 2026 RENOVA Centro Médico. Todos los derechos reservados.
            </p>
            <div className="flex flex-wrap justify-center sm:justify-end gap-4 sm:gap-6">
              <Link
                to="/politica-de-privacidad"
                className="text-brand-green-200/40 hover:text-brand-gold-400 transition-colors text-xs sm:text-sm cursor-pointer hover:-translate-y-0.5 inline-block"
              >
                Política de Privacidad
              </Link>
              <Link
                to="/terminos-y-servicios"
                className="text-brand-green-200/40 hover:text-brand-gold-400 transition-colors text-xs sm:text-sm cursor-pointer hover:-translate-y-0.5 inline-block"
              >
                Términos de Servicio
              </Link>
              <a
                href="https://readdy.ai/?ref=logo"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="text-brand-green-200/40 hover:text-brand-gold-400 transition-colors text-xs sm:text-sm cursor-pointer hover:-translate-y-0.5 inline-block"
              >
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </footer>
  );
}